import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiRemote } from "@/lib/apiRemote";
import { unwrapListings } from "@/lib/adapters";
import { cachedGet, peek, rememberListings } from "@/lib/cache";
import PropertyCard from "@/components/PropertyCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MagnifyingGlass, FunnelSimple, X, MapTrifold, List, BookmarkSimple, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ListingsMap from "@/components/ListingsMap";
import { PrefectureSelect } from "@/components/LocationFields";
import { useGuinea, findPrefecture, matchesCommune, norm, conakryCommuneOf } from "@/lib/guinea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { key: "", label: "Toutes catégories" },
  { key: "maison", label: "Maison" },
  { key: "appartement", label: "Appartement" },
  { key: "terrain", label: "Terrain" },
  { key: "bureau", label: "Bureau" },
  { key: "commercial", label: "Local commercial" },
  { key: "chantier", label: "Chantier" },
];


function DebouncedNumberInput({ value, onCommit, delay = 500, ...props }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  useEffect(() => {
    if ((v || "") === (value || "")) return;
    const t = setTimeout(() => onCommit(v), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
  return <Input type="number" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} {...props} />;
}

const CLIENT_ONLY = ["region", "commune", "quartier"];
const POPULAR_QUARTIERS = ["Kipé", "Nongo", "Lambanyi", "Kaporo", "Taouyah", "Sonfonia", "Kaloum", "Dixinn", "Matoto Centre", "Cosa"];

const buildParams = (filters) => ({
  ...Object.fromEntries(
    Object.entries(filters).filter(([k, v]) => v !== "" && v != null && !CLIENT_ONLY.includes(k))
  ),
  limit: filters.region || filters.commune || filters.quartier ? 100 : 30,
});

function applyGeoFilters(items, filters, geo) {
  if (!geo || (!filters.region && !filters.commune && !filters.quartier)) return items;
  const region = geo.regions.find((r) => r.id === filters.region);
  const inRegion = region ? new Set(region.prefectures.map((p) => norm(p.name))) : null;
  const pref = findPrefecture(geo, filters.city);
  return items.filter(
    (l) =>
      (!inRegion || inRegion.has(norm(l.city))) &&
      (!filters.commune || matchesCommune(pref, filters.commune, l.quartier)) &&
      (!filters.quartier || norm(l.quartier) === norm(filters.quartier))
  );
}

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const reqId = useRef(0);
  const geo = useGuinea();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [favSet, setFavSet] = useState(new Set());
  const [q, setQ] = useState(sp.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState("list");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const filters = {
    q: sp.get("q") || "",
    property_type: sp.get("category") || "",
    city: sp.get("city") || "",
    region: sp.get("region") || "",
    commune: sp.get("commune") || "",
    quartier: sp.get("quartier") || "",
    transaction_type: sp.get("transaction_type") || "",
    min_price: sp.get("min_price") || "",
    max_price: sp.get("max_price") || "",
    bedrooms: sp.get("chambres") || "",
  };

  const load = async () => {
    const id = ++reqId.current;
    const params = buildParams(filters);
    const stale = peek("/listings", params);
    if (stale) {
      const u = unwrapListings(stale);
      const f = applyGeoFilters(u.items, filters, geo);
      setItems(f);
      setTotal(f.length < u.items.length ? f.length : u.total);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const data = await cachedGet("/listings", params);
      if (id !== reqId.current) return;
      const u = unwrapListings(data);
      rememberListings(u.items);
      const f = applyGeoFilters(u.items, filters, geo);
      setItems(f);
      setTotal(f.length < u.items.length ? f.length : u.total);
    } catch {
      if (id === reqId.current && !stale) toast.error("Connexion lente — impossible de charger les annonces. Réessayez.");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.toString(), !!geo]);

  useEffect(() => {
    if (!user) return;
    apiRemote
      .get("/favorites")
      .then(({ data }) => {
        const ids = (data.listings || []).map((l) => l.id || l.listing_id);
        setFavSet(new Set(ids));
      })
      .catch(() => {});
  }, [user]);

  const updateFilter = (key, value, reset = []) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value == null) next.delete(key);
    else next.set(key, value);
    reset.forEach((k) => next.delete(k));
    setSp(next);
  };

  const selectedPref = findPrefecture(geo, filters.city);
  const communeObj = selectedPref?.communes.find((c) => c.name === filters.commune);
  const pickQuartier = (name) => {
    const conakry = findPrefecture(geo, "Conakry");
    const isCommune = conakry?.communes.some((c) => c.name === name);
    const next = new URLSearchParams(sp);
    ["region", "commune", "quartier"].forEach((k) => next.delete(k));
    next.set("city", "Conakry");
    next.set("commune", isCommune ? name : conakryCommuneOf(conakry, name));
    if (!isCommune) next.set("quartier", name);
    setSp(next);
  };
  const alertHref = `/alertes?${new URLSearchParams(
    Object.fromEntries([["city", selectedPref?.name], ["commune", filters.commune]].filter(([, v]) => v))
  )}`;
  const regionGeo = geo && filters.region ? { ...geo, regions: geo.regions.filter((r) => r.id === filters.region) } : geo;

  const submitSearch = (e) => {
    e.preventDefault();
    updateFilter("q", q);
  };

  const clearFilters = () => {
    setQ("");
    setSp(new URLSearchParams());
  };

  const toggleFav = async (listing_id) => {
    if (!user) return toast.error("Connectez-vous pour ajouter aux favoris");
    if (favSet.has(listing_id)) {
      await apiRemote.delete(`/favorites/${listing_id}`);
      const next = new Set(favSet);
      next.delete(listing_id);
      setFavSet(next);
      toast.success("Retiré des favoris");
    } else {
      await apiRemote.post(`/favorites/${listing_id}`);
      const next = new Set(favSet);
      next.add(listing_id);
      setFavSet(next);
      toast.success("Ajouté aux favoris");
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== "q" && v).length;

  const saveSearch = async () => {
    if (!user) {
      toast.error("Connectez-vous pour sauvegarder");
      return;
    }
    if (!saveName.trim()) return;
    try {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v != null)
      );
      await api.post("/saved-searches", { name: saveName.trim(), filters: clean });
      toast.success("Recherche sauvegardée");
      setSaveOpen(false);
      setSaveName("");
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <div className="mb-6 flex flex-col gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Rechercher</span>
        <h1 className="font-display text-3xl font-black md:text-4xl">Toutes les annonces</h1>
      </div>

      <form
        onSubmit={submitSearch}
        className="mb-6 flex gap-2 rounded-full border border-border bg-card p-2"
        data-testid="search-form"
      >
        <div className="flex flex-1 items-center gap-2 px-3">
          <MagnifyingGlass size={20} className="text-muted-foreground" />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ville, quartier, mot-clé…"
            className="border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setShowFilters((s) => !s)}
          data-testid="toggle-filters"
          className="btn-pill rounded-full font-semibold"
        >
          <FunnelSimple size={18} className="mr-1" />
          Filtres
          {activeFilterCount > 0 && (
            <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </Button>
        <Button
          type="submit"
          data-testid="search-submit"
          className="btn-pill rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
        >
          Rechercher
        </Button>
      </form>

      {showFilters && (
        <div
          className="mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-border bg-card p-6 md:grid-cols-3 lg:grid-cols-6"
          data-testid="filters-panel"
        >
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Catégorie
            </label>
            <Select value={filters.property_type || "all"} onValueChange={(v) => updateFilter("category", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-category">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.key || "all"} value={c.key || "all"}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Région
            </label>
            <Select
              value={filters.region || "all"}
              onValueChange={(v) => updateFilter("region", v === "all" ? "" : v, ["city", "commune", "quartier"])}
            >
              <SelectTrigger data-testid="filter-region">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les régions</SelectItem>
                {(geo?.regions || []).map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`filter-region-opt-${r.id}`}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="[&_button]:mt-0">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Préfecture / ville
            </label>
            <PrefectureSelect
              geo={regionGeo}
              value={selectedPref?.name || filters.city}
              allowAll
              testid="filter-city"
              placeholder="Toutes"
              onChange={(v) => updateFilter("city", v, ["commune", "quartier"])}
            />
          </div>
          {selectedPref && (
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Commune
              </label>
              <Select value={filters.commune || "all"} onValueChange={(v) => updateFilter("commune", v === "all" ? "" : v, ["quartier"])}>
                <SelectTrigger data-testid="filter-commune">
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">Toutes les communes</SelectItem>
                  {selectedPref.communes.map((c) => (
                    <SelectItem key={c.name} value={c.name} data-testid={`filter-commune-opt-${norm(c.name)}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {communeObj?.quartiers && (
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Quartier
              </label>
              <Select value={filters.quartier || "all"} onValueChange={(v) => updateFilter("quartier", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-quartier">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">Tous les quartiers</SelectItem>
                  {communeObj.quartiers.map((q) => (
                    <SelectItem key={q} value={q} data-testid={`filter-quartier-opt-${norm(q)}`}>
                      {q}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Type
            </label>
            <Select
              value={filters.transaction_type || "all"}
              onValueChange={(v) => updateFilter("transaction_type", v === "all" ? "" : v)}
            >
              <SelectTrigger data-testid="filter-transaction">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Vente & Location</SelectItem>
                <SelectItem value="vente">Vente</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Prix min
            </label>
            <DebouncedNumberInput
              data-testid="filter-min-price"
              value={filters.min_price}
              onCommit={(v) => updateFilter("min_price", v)}
              placeholder="GNF"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Prix max
            </label>
            <DebouncedNumberInput
              data-testid="filter-max-price"
              value={filters.max_price}
              onCommit={(v) => updateFilter("max_price", v)}
              placeholder="GNF"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Chambres min
            </label>
            <DebouncedNumberInput
              data-testid="filter-chambres"
              value={filters.bedrooms}
              onCommit={(v) => updateFilter("chambres", v)}
              placeholder="1+"
            />
          </div>
          <div className="md:col-span-3 lg:col-span-6">
            <Button
              variant="ghost"
              onClick={clearFilters}
              data-testid="clear-filters"
              className="btn-pill rounded-full font-semibold text-primary"
            >
              <X size={16} className="mr-1" /> Réinitialiser
            </Button>
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="quartier-chips">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Conakry :</span>
        {POPULAR_QUARTIERS.map((q) => {
          const active = norm(filters.quartier || (!filters.quartier && filters.commune)) === norm(q);
          return (
            <button
              key={q}
              type="button"
              onClick={() => pickQuartier(q)}
              data-testid={`chip-quartier-${norm(q)}`}
              className={`btn-pill rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary hover:text-primary"
              }`}
            >
              {q}
            </button>
          );
        })}
        {selectedPref && (
          <Link to={alertHref} data-testid="create-alert-link" className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
            <BellRinging size={14} weight="fill" /> Alerte pour {filters.commune || selectedPref.name}
          </Link>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-muted-foreground" data-testid="results-count">
          {loading ? "Chargement…" : `${total} annonce${total > 1 ? "s" : ""} trouvée${total > 1 ? "s" : ""}`}
        </div>
        <div className="flex items-center gap-2">
          {user && (filters.q || activeFilterCount > 0) && (
            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="save-search-btn"
                  className="btn-pill rounded-full font-semibold"
                >
                  <BookmarkSimple size={14} weight="duotone" className="mr-1" />
                  Sauvegarder
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nommer la recherche</DialogTitle>
                </DialogHeader>
                <Input
                  data-testid="save-search-name"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="Ex: Villas Conakry -300M"
                />
                <Button
                  onClick={saveSearch}
                  disabled={!saveName.trim()}
                  data-testid="save-search-confirm"
                  className="btn-pill rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
                >
                  Sauvegarder
                </Button>
              </DialogContent>
            </Dialog>
          )}
          <div className="flex items-center gap-1 rounded-full bg-muted p-1" data-testid="view-toggle">
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`btn-pill flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${
                view === "list" ? "bg-background shadow" : "text-muted-foreground"
              }`}
            >
              <List size={14} /> Liste
            </button>
            <button
              onClick={() => setView("map")}
              data-testid="view-map"
              className={`btn-pill flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${
                view === "map" ? "bg-background shadow" : "text-muted-foreground"
              }`}
            >
              <MapTrifold size={14} /> Carte
            </button>
          </div>
        </div>
      </div>

      {view === "map" && !loading && (
        <div className="mb-6">
          <ListingsMap listings={items} focus={selectedPref ? [selectedPref.lat, selectedPref.lng] : null} />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-96 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
          <h3 className="font-display text-xl font-bold">Aucune annonce ne correspond</h3>
          <p className="mt-2 text-sm text-muted-foreground">Essayez avec d'autres filtres.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((l) => (
            <PropertyCard
              key={l.listing_id}
              listing={l}
              isFavorite={favSet.has(l.listing_id)}
              onToggleFavorite={toggleFav}
            />
          ))}
        </div>
      )}
    </div>
  );
}
